// @flow
import * as React from 'react';

import useVisibilityChange from './useVisibilityChange';
import useLatest from './useLatest';
import Time from 'common/constants/time';

type PollerOptions = {
    /**
     * How frequently should we call the onPoll handler. Defaults to 10 seconds.
     */
    interval?: number,

    /**
     * Should we immediately trigger the callback.
     */
    immediate?: boolean,

    /**
     * enabled/disable polling.
     */
    enabled?: boolean
};

export default function usePoller(
    callback: () => void,
    {
        interval = 10 * Time.sec,
        immediate = true,
        enabled = true
    }: PollerOptions = {}
) {
    const callbackRef = useLatest(callback);
    const isVisible = useVisibilityChange();

    const isPolling = isVisible && enabled;

    React.useEffect(() => {
        if (!isPolling) {
            return;
        }

        let id = null;

        const schedulePoll = () => {
            unschedulePoll();
            id = setTimeout(poll, interval);
        };

        const unschedulePoll = () => {
            clearTimeout(id);
        };

        const poll = () => {
            Promise.resolve(callbackRef.current()).finally(schedulePoll);
        };

        if (immediate) {
            poll();
        } else {
            schedulePoll();
        }

        return unschedulePoll;
    }, [interval, immediate, isPolling, callbackRef]);
}
